import { Smartphone } from "lucide-react";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { useLanguage } from "../contexts/LanguageContext";

export function PushNotificationSettingsCard({
	notifPrefs,
	pushSupported,
	pushServerConfigured,
	pushBusy,
	savingNotifs,
	onPushEnabledChange,
	onNotifChange,
	showTestButton = false,
	onSendPushTest,
	testId,
}) {
	const { language } = useLanguage();

	return (
		<Card className="bg-card border-border rounded-sm" data-testid={testId}>
			<CardHeader>
				<CardTitle className="font-heading text-xl flex items-center gap-2">
					<Smartphone className="w-5 h-5 text-volt" />
					{language === "it" ? "Notifiche Push" : "Push Notifications"}
				</CardTitle>
				<CardDescription>
					{language === "it"
						? "Configura i flussi push per atleta e coach (dispositivo corrente)"
						: "Configure push flows for athlete and coach (current device)"}
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="flex items-center justify-between gap-4 p-3 rounded-sm border border-volt/30 bg-volt/5">
					<div className="flex-1">
						<Label
							htmlFor="push-enabled"
							className="text-sm font-medium cursor-pointer"
						>
							{language === "it"
								? "Abilita notifiche push"
								: "Enable push notifications"}
						</Label>
						<p className="text-xs text-muted-foreground mt-1">
							{pushSupported
								? pushServerConfigured
									? language === "it"
										? "Attiva/disattiva le notifiche push sul browser corrente"
										: "Enable or disable push notifications on this browser"
									: language === "it"
										? "Push non configurate lato server"
										: "Push not configured on server"
								: language === "it"
									? "Browser non compatibile con notifiche push"
									: "Browser does not support push notifications"}
						</p>
					</div>
					<Switch
						id="push-enabled"
						checked={!!notifPrefs.push_enabled}
						onCheckedChange={onPushEnabledChange}
						disabled={
							!pushSupported ||
							!pushServerConfigured ||
							pushBusy ||
							savingNotifs
						}
						className="shrink-0"
					/>
				</div>

				<div className="flex items-center justify-between">
					<div>
						<Label htmlFor="push-message" className="text-sm font-medium">
							{language === "it" ? "Nuovi messaggi" : "New messages"}
						</Label>
						<p className="text-xs text-muted-foreground">
							{language === "it"
								? "Invia una push quando arriva un nuovo messaggio"
								: "Send a push when a new message arrives"}
						</p>
					</div>
					<Switch
						id="push-message"
						checked={!!notifPrefs.push_new_message}
						onCheckedChange={(v) => onNotifChange("push_new_message", v)}
						disabled={
							!pushSupported ||
							!pushServerConfigured ||
							!notifPrefs.push_enabled ||
							pushBusy ||
							savingNotifs
						}
					/>
				</div>

				<div className="flex items-center justify-between">
					<div>
						<Label
							htmlFor="push-program-and-week"
							className="text-sm font-medium"
						>
							{language === "it" ? "Programmi e settimane" : "Programs & weeks"}
						</Label>
						<p className="text-xs text-muted-foreground">
							{language === "it"
								? "Invia una push per nuovi programmi e nuove settimane pubblicate dal coach"
								: "Send push for new programs and weeks published by coach"}
						</p>
					</div>
					<Switch
						id="push-program-and-week"
						checked={!!notifPrefs.push_new_program_and_week}
						onCheckedChange={(v) =>
							onNotifChange("push_new_program_and_week", v)
						}
						disabled={
							!pushSupported ||
							!pushServerConfigured ||
							!notifPrefs.push_enabled ||
							pushBusy ||
							savingNotifs
						}
					/>
				</div>

				{!pushServerConfigured && pushSupported && (
					<p className="text-xs text-muted-foreground">
						{language === "it"
							? "Mancano le chiavi VAPID sul backend: il prompt non può comparire finché non vengono configurate."
							: "VAPID keys are missing on backend: browser prompt cannot appear until configured."}
					</p>
				)}

				{!notifPrefs.push_enabled && (
					<p className="text-xs text-muted-foreground">
						{language === "it"
							? "Abilita prima le push per attivare i singoli flussi."
							: "Enable push first to activate individual flows."}
					</p>
				)}

				{showTestButton && (
					<Button
						variant="outline"
						onClick={onSendPushTest}
						disabled={
							!pushSupported ||
							!pushServerConfigured ||
							!notifPrefs.push_enabled ||
							pushBusy
						}
						className="w-full rounded-sm"
					>
						{language === "it"
							? "Invia notifica di test"
							: "Send test notification"}
					</Button>
				)}
			</CardContent>
		</Card>
	);
}
